import { clinicalReferencePublications, unverifiedClinicalReferences, clinicalReferenceApplicabilityNotes } from '../data/clinical-reference-catalog';

const publicationsByCitation = new Map(Object.entries(clinicalReferencePublications));
const unverifiedNotesByCitation = new Map(Object.entries(unverifiedClinicalReferences));

export interface ClinicalSource {
  citation: string;
  reference: string;
  scope: 'rule' | 'condition' | 'biomarker';
  origin: 'workbook' | 'snapshot';
  sourceFile?: string;
  sheet?: string;
}

export interface ClinicalSourceMetadata {
  sources?: ClinicalSource[];
  sourceStatus?: 'available' | 'partial' | 'not_stored';
  missingSources?: string[];
  citation?: string;
}

export function readClinicalSources(value: Record<string, unknown>): ClinicalSourceMetadata {
  const sources = Array.isArray(value.sources) ? value.sources.filter((source): source is ClinicalSource => (
    !!source && typeof source === 'object'
    && typeof source.citation === 'string' && source.citation.trim().length > 0
    && typeof source.reference === 'string'
    && ['rule', 'condition', 'biomarker'].includes(source.scope)
  )) : undefined;
  return {
    ...(sources ? { sources } : {}),
    ...(['available', 'partial', 'not_stored'].includes(String(value.sourceStatus))
      ? { sourceStatus: value.sourceStatus as ClinicalSourceMetadata['sourceStatus'] } : {}),
    ...(Array.isArray(value.missingSources) ? { missingSources: value.missingSources.filter((item): item is string => typeof item === 'string') } : {}),
    ...(typeof value.citation === 'string' && value.citation.trim() ? { citation: value.citation } : {}),
  };
}

// The insulin-resistance workbook citation was incorrectly changed to 2026.
// Correct that known saved text for older plans without rewriting unrelated citations.
const incorrectInsulinResistanceCitation = 'CalorieScience practitioner default · ADA SoC 2026 (individualise macros) · SACN total-fat DRV';

function isGeneralNutrientReference(reference: string): boolean {
  const label = reference.replace(/[_()]/g, ' ').replace(/\s+/g, ' ').trim();
  // General dietary reference values belong in DRV/Client Targets. Named clinical
  // publications (e.g. SACN Lower Carb 2021 or SACN Vit D 2016) remain visible.
  return /\b(?:DRV|DRI|RDA|dietary reference (?:values|intakes))\b/i.test(label)
    || /^(?:(?:UK )?SACN(?:\s*\/\s*COMA)?|ICMR(?:[- ]NIN)?(?: India)?|EFSA(?: EU)?|IOM(?: USA)?|NAM|WHO\s*\/\s*FAO)(?:\s+\d{4})?$/i.test(label)
    || /^EFSA\s*\/\s*SACN$/i.test(label)
    || /^EFSA (?:Protein|Iodine)(?:\s+\d{4})?$/i.test(label);
}

export interface ClinicalDisplayReference {
  label: string;
  title?: string;
  url?: string;
  note?: string;
  status: 'edition_verified' | 'revision_unrecorded' | 'unverified';
}

/** Exact aliases only: never infer a year from the current date or a different edition. */
export function clinicalDisplayReferences(issue: ClinicalSourceMetadata): ClinicalDisplayReference[] {
  const sources = issue.sources?.filter(source => source.citation.trim()) ?? [];
  const references = sources.length ? sources : issue.citation ? [{ citation: issue.citation, reference: '' }] : [];
  const result = new Map<string, ClinicalDisplayReference>();
  for (const source of references) {
    const citation = source.citation.trim() === incorrectInsulinResistanceCitation
      ? source.citation.replace('ADA SoC 2026', 'ADA SoC 2024') : source.citation;
    for (const part of citation.split(/[·;]/).flatMap(part => part.trim() === 'NICE NG3 / NG17' ? ['NICE NG3', 'NICE NG17'] : [part])) {
      const reference = part.replace(/\s*\((?:individualise macros|label-derived|derived|treat as T1D)\)/gi, '').trim();
      if (!reference || /^(?:CalorieScience\b|This document$|Derived$|T\d+$)/i.test(reference) || isGeneralNutrientReference(reference)) continue;
      const publication = publicationsByCitation.get(reference);
      const contextKey = source.reference.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const applicability = clinicalReferenceApplicabilityNotes[reference]?.[contextKey];
      const entry: ClinicalDisplayReference = publication ? {
        label: publication.year
          ? `${publication.name} (${publication.yearKind === 'publication' ? 'first published ' : ''}${publication.year})`
          : publication.name,
        title: publication.title,
        url: publication.url,
        status: publication.yearKind === 'edition' ? 'edition_verified' : 'revision_unrecorded',
        note: [
          publication.yearKind === 'publication' ? 'Cited revision not recorded.' : !publication.year ? 'Cited version/year not recorded.' : '',
          publication.replacedBy ? `Replaced by ${publication.replacedBy}; the rule still cites the older guideline.` : '',
          applicability,
        ].filter(Boolean).join(' ') || undefined,
      } : {
        label: `${reference} — guideline/year unverified`, status: 'unverified',
        note: unverifiedNotesByCitation.get(reference) ?? 'No verified publication is registered for this exact citation.',
      };
      // Preserve context-specific review notes even when another condition uses the same publication.
      result.set(`${entry.label}|${entry.note ?? ''}`, entry);
    }
  }
  return [...result.values()];
}

/** Text-only surfaces receive the same bibliographic caveats as the source disclosure. */
export function clinicalSourceLabels(issue: ClinicalSourceMetadata): string[] {
  return clinicalDisplayReferences(issue).map(source =>
    `${source.label}${source.title ? ` — ${source.title}` : ''}${source.note ? ` — ${source.note}` : ''}`);
}

/** Text-only confirmations and notifications show the same source labels as insight cards. */
export function formatClinicalIssueWithSources(issue: ClinicalSourceMetadata & { message?: unknown }): string {
  const references = clinicalSourceLabels(issue);
  const message = String(issue.message ?? '').trim();
  return references.length ? `${message}\nSources: ${references.join('; ')}`.trim() : message;
}

export function clinicalErrorIssueMessages(error: unknown): string[] {
  if (!error || typeof error !== 'object') return [];
  const body = (error as { response?: { data?: Record<string, unknown> } }).response?.data;
  if (!body || typeof body !== 'object') return [];
  const details = body.details && typeof body.details === 'object' ? body.details as Record<string, unknown> : undefined;
  const issues = Array.isArray(details?.blockingIssues) ? details.blockingIssues : body.blockingIssues;
  if (!Array.isArray(issues)) return [];
  return [...new Set(issues.flatMap(issue => {
    if (!issue || typeof issue !== 'object' || typeof issue.message !== 'string' || !issue.message.trim()) return [];
    return [formatClinicalIssueWithSources({ message: issue.message, ...readClinicalSources(issue) })];
  }))];
}
